import React, { useState, useEffect, useCallback } from "react";
import { GripVertical } from "lucide-react";

const FillInTheBlanksDraggable = ({
  sentence,
  options,
  submitted,
  onOptionsChange,
}) => {
  const parts = sentence.split(/(__add_blank__|<span[^>]*>.*?<\/span>)/);
  const placeholderIndices = parts
    .map((part, index) => (part.startsWith("<span") ? index : null))
    .filter((index) => index !== null);
  const correctAnswers = parts
    .filter((part) => part.startsWith("<span") && part.endsWith("</span>"))
    .map((part) => part.replace(/<span[^>]*>|<\/span>/g, ""));
  const initialReplacedOptions = Array(placeholderIndices.length).fill(null);
  const [replacedOptions, setReplacedOptions] = useState(initialReplacedOptions);
  const [isCorrect, setIsCorrect] = useState(
    Array(placeholderIndices.length).fill(false)
  );

  const checkAnswers = useCallback(() => {
    if (submitted) {
      const newIsCorrect = replacedOptions.map(
        (option, index) => option === correctAnswers[index]
      );
      setIsCorrect(newIsCorrect);
    }
  }, [submitted, replacedOptions, correctAnswers]);

  useEffect(() => {
    checkAnswers();
  }, [submitted]);

  useEffect(() => {
    if (!submitted) {
      setReplacedOptions(initialReplacedOptions);
      setIsCorrect(Array(placeholderIndices.length).fill(false));
    }
  }, [submitted]);

  const handleDragStart = (event, option) => {
    event.dataTransfer.setData("text/plain", option);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event, index) => {
    if (submitted) return;
    event.preventDefault();
    const option = event.dataTransfer.getData("text/plain");
    replacePlaceholder(index, option);
  };

  const replacePlaceholder = (index, option) => {
    const newReplacedOptions = [...replacedOptions];
    newReplacedOptions[index] = option;
    setReplacedOptions(newReplacedOptions);
    onOptionsChange(newReplacedOptions);
  };

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <p className="text-sm font-semibold uppercase text-purple-700">
          Passage
        </p>
        <div className="mt-4 rounded-md bg-slate-50 p-4 text-sm leading-8 text-gray-700 ring-1 ring-slate-100 sm:text-base">
        {parts.map((part, index) => {
          if (part.startsWith("<span") && part.endsWith("</span>")) {
            const placeholderIndex = placeholderIndices.indexOf(index);
            const option = replacedOptions[placeholderIndex];

            return (
              <span
                key={index}
                onDragOver={handleDragOver}
                onDrop={(event) => handleDrop(event, placeholderIndex)}
                className={`mx-1 inline-flex min-h-10 min-w-24 items-center justify-center rounded-md border px-3 py-2 text-sm font-bold shadow-sm transition ${
                  submitted
                    ? isCorrect[placeholderIndex]
                      ? "border-green-200 bg-green-50 text-green-700"
                      : "border-red-200 bg-red-50 text-red-700"
                    : "border-purple-200 bg-white text-purple-800 ring-1 ring-purple-100"
                }`}
              >
                {option !== null ? option : `__${placeholderIndex + 1}__`}
              </span>
            );
          } else if (!part.includes("__add_blank__")) {
            return <span key={index}>{part}</span>;
          } else {
            return null;
          }
        })}
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4">
          <p className="text-sm font-semibold uppercase text-purple-700">
            Word bank
          </p>
          <h2 className="mt-1 text-lg font-extrabold text-gray-900">
            Drag words into the blanks
          </h2>
        </div>

        <div className="flex flex-wrap gap-2 rounded-md bg-slate-50 p-3 ring-1 ring-slate-100">
        {options.map((option, index) => (
          <div
            key={index}
            draggable={!submitted}
            onDragStart={(event) => handleDragStart(event, option)}
            className="inline-flex cursor-move items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-gray-700 shadow-sm transition hover:border-purple-200 hover:bg-purple-50 hover:text-purple-800"
          >
            <GripVertical className="h-4 w-4 text-gray-400" aria-hidden="true" />
            {option}
          </div>
        ))}
        </div>
      </div>
    </div>
  );
};

export default FillInTheBlanksDraggable;
